import { useState } from 'react';
import { Fingerprint, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { useVault } from './VaultContext';
import { toast } from 'sonner';

interface BiometricSetupProps {
  open: boolean;
  onClose: () => void;
  email: string;
  password: string;
}

/**
 * Sheet to offer biometric setup after successful vault unlock.
 * Shows when biometric is available but not yet enabled.
 */
export function BiometricSetup({ open, onClose, email, password }: BiometricSetupProps) {
  const { setupBiometric, isBiometricAvailable } = useVault();
  const [isSettingUp, setIsSettingUp] = useState(false);

  if (!isBiometricAvailable) {
    return null;
  }

  const handleSetup = async () => {
    setIsSettingUp(true);
    try {
      await setupBiometric(email, password);
      toast.success('FaceID включён', {
        description: 'Теперь вы можете разблокировать пароли по FaceID',
      });
      onClose();
    } catch (error) {
      console.error('Biometric setup failed:', error);
      toast.error('Не удалось настроить FaceID', {
        description: error instanceof Error ? error.message : 'Попробуйте ещё раз',
      });
    } finally {
      setIsSettingUp(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent side="bottom" className="h-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Fingerprint className="h-5 w-5" />
            Включить FaceID?
          </SheetTitle>
          <SheetDescription>
            В следующий раз вы сможете разблокировать пароли одним касанием, без ввода master password
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-3 mt-6">
          <Button
            onClick={handleSetup}
            disabled={isSettingUp}
            className="w-full"
            size="lg"
          >
            {isSettingUp ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Настройка...
              </>
            ) : (
              <>
                <Fingerprint className="h-4 w-4 mr-2" />
                Включить FaceID
              </>
            )}
          </Button>

          <Button
            variant="ghost"
            onClick={handleSkip}
            disabled={isSettingUp}
            className="w-full"
          >
            <X className="h-4 w-4 mr-2" />
            Не сейчас
          </Button>
        </div>

        <p className="text-xs text-muted-foreground text-center mt-4">
          Ваш master password будет надёжно зашифрован и сохранён на устройстве
        </p>
      </SheetContent>
    </Sheet>
  );
}
